import {
  Input as BaseInput,
  NumberInput as BaseNumberInput,
} from '@/components_deprecated'
import { TextArea as BaseTextArea } from '@/components_deprecated/TextArea'
import { AutoCompleteCountryCodes } from '@/utils/consts'
import { useGoogleAutocomplete, useMap } from '@/utils/hooks'
import type { UseGoogleAutocompleteOptions } from '@/utils/hooks/useGoogleAutocomplete/types'
import type { FormErrors, FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useEffectEvent, useRef, useState } from 'react'
import styled from 'styled-components'
import { VehicleSelect } from '.'
import type { EditIssueForm } from '../types'
import { DetailsField, DetailsSection } from './issue-details-layout'
import type { UseIssueFieldConfig } from '../field-config'
import {
  ConfiguredField,
  IssueFormField,
  isDetailsSectionVisible,
} from '../field-config'

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`
const Input = styled(BaseInput).attrs({
  grayed: true,
  height: '3rem',
})<{ error?: string | null }>`
  input {
    ${props =>
      props.error &&
      `
      border-color: ${props.theme.colors.red};
      &:focus {
        border-color: ${props.theme.colors.red};
      }
    `}
  }
`
const NumberInput = styled(BaseNumberInput).attrs({
  grayed: true,
  width: 'auto',
  height: '3rem',
})`
  input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`
interface TextAreaProps {
  scale?: number
}

const TextArea = styled(BaseTextArea).attrs({
  background: 'quaternary',
})<TextAreaProps>`
  textarea {
    height: 5rem;
  }

  ${({ theme }) => theme.media.mobile} {
    textarea {
      height: ${({ scale = 1 }) => 5 * scale}rem;
    }
  }
`

const PlaceEditor = styled(Editor)`
  flex-direction: row;
`

const CurrencyEditor = styled(Editor)`
  flex-direction: row;

  div.currency {
    display: flex;
    justify-content: center;
    align-items: center;

    height: 3rem;
    padding: 0 0.5rem;

    text-transform: uppercase;
    font-weight: 700;
    font-size: 0.9rem;

    background-color: white;
    border: 1px solid ${props => props.theme.colors.gray9};
    border-left-width: 0;

    border-top-right-radius: ${props => props.theme.sizes.defaultBorderRadius};
    border-bottom-right-radius: ${props =>
      props.theme.sizes.defaultBorderRadius};
  }
`

interface CreateDetailsProps {
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>

  fieldConfig: UseIssueFieldConfig

  onLocationUpdate: (
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) => void
  onChange: FormOnChangeFn<EditIssueForm>
}

export const CreateDetails: React.FC<CreateDetailsProps> = ({
  form,
  errors,

  fieldConfig,

  onLocationUpdate,
  onChange,
}) => {
  const translations = useTranslate({
    location: 'common.location',
    equipment: 'issues.equipment-material',
    immediateAction: 'issues.immediate-action',
    action: 'issues.action',
    reason: 'issues.reason',
    description: 'common.description',
    damage: 'issues.damage-description',
    suggestedSolution: 'issues.suggested-solution',
    costEstimate: 'issues.cost-estimation',
    locationDescription: 'issues.location-description',
    solution: 'issues.final-solution',
    actualCost: 'issues.actual-cost',

    sectionLocation: 'issues.section-location',
    sectionEvent: 'issues.section-event',
    sectionResolution: 'issues.section-resolution',
    sectionCost: 'issues.section-cost',
  })
  const prevPlacename = useRef<string | null>(null)
  const [descriptionPlaceIdMap, { set: descriptionPlaceIdMapSet }] = useMap<
    string,
    string
  >()

  const [triggerGeocoding, setTriggerGeocoding] = useState(false)

  const handleFindPlacename = useEffectEvent(() => {
    prevPlacename.current = form.placename
    const service = new google.maps.Geocoder()
    const placeIdValue = descriptionPlaceIdMap.get(prevPlacename.current)

    const geocodeOptions =
      // if we have a placeID from the autocomplete this simply needs to be a lookup with the ID, otherwise we try to find something with geocoding
      placeIdValue !== undefined
        ? { placeId: placeIdValue }
        : { address: form.placename }

    void service.geocode(geocodeOptions, onLocationUpdate)
  })

  const handleTriggerGeocoding = useEffectEvent(() => {
    if (!triggerGeocoding) return

    handleFindPlacename()
    setTriggerGeocoding(false)
  })

  useEffect(() => {
    prevPlacename.current = form.placename

    if (typeof google === 'undefined' || form.placename.trim() === '') return

    handleTriggerGeocoding()
  }, [form.placename, prevPlacename, triggerGeocoding, onLocationUpdate])

  const predictions = useGoogleAutocomplete(form.placename, {
    componentRestrictions: {
      country: AutoCompleteCountryCodes,
    },
  } as UseGoogleAutocompleteOptions)

  const autoCompleteSource = predictions.predictions?.map<string>(pred => {
    // The description field is the human readable value, whereas if we pick an option from here
    // we want to use the ID related to said description. We do this by making use of a hashmap which encodes a
    // string with an ID which we can use when we want to perform geocoding. If the key already exists we don't bother.
    if (!descriptionPlaceIdMap.has(pred.description))
      descriptionPlaceIdMapSet(pred.description, pred.place_id)

    return pred.description
  })

  function handlePlacenameKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>
  ) {
    const { key } = event
    if (key === 'Enter') {
      setTriggerGeocoding(true)
    }
  }

  function handleAutoCompleteItemSelect(placename: string) {
    onChange('placename')(placename)
    setTriggerGeocoding(true)
  }

  // A section card is only worth drawing when something inside it will show.
  // The close-out fields (final solution, actual cost) keep their card alive
  // while the form is being closed.
  const showLocationSection = isDetailsSectionVisible(
    fieldConfig,
    'create',
    'LOCATION'
  )
  const showEventSection = isDetailsSectionVisible(
    fieldConfig,
    'create',
    'EVENT'
  )
  const showResolutionSection = isDetailsSectionVisible(
    fieldConfig,
    'create',
    'RESOLUTION',
    form.closed
  )
  const showCostSection = isDetailsSectionVisible(
    fieldConfig,
    'create',
    'COST',
    form.closed
  )

  return (
    <>
      {showLocationSection && (
        <DetailsSection
          title={translations.sectionLocation}
          icon="location-dot"
        >
          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.LOCATION_PLACENAME}
            mode="create"
            label={translations.location}
            elevated
          >
            <PlaceEditor>
              <Input
                value={form.placename}
                fullWidth
                autoCompleteSource={autoCompleteSource}
                onAutoCompleteItemSelect={handleAutoCompleteItemSelect}
                onChange={onChange('placename')}
                onKeyDown={handlePlacenameKeyDown}
                error={errors.placename}
              />
            </PlaceEditor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.VEHICLE_PLATE_NUMBER}
            mode="create"
            label={translations.equipment}
          >
            <Editor>
              <VehicleSelect
                value={form.regNr}
                className="w-full"
                onChange={onChange('regNr')}
              />
            </Editor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.LOCATION_DESCRIPTION}
            mode="create"
            label={translations.locationDescription}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.locationDescription}
                error={errors.locationDescription}
                fullWidth
                onChange={onChange('locationDescription')}
              />
            </Editor>
          </ConfiguredField>
        </DetailsSection>
      )}

      {showEventSection && (
        <DetailsSection
          title={translations.sectionEvent}
          icon="triangle-exclamation"
        >
          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.ISSUE_DESCRIPTION}
            mode="create"
            label={translations.description}
            full
          >
            <Editor>
              <TextArea
                scale={3}
                value={form.description}
                error={errors.description}
                fullWidth
                onChange={onChange('description')}
              />
            </Editor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.DAMAGE_DESCRIPTION}
            mode="create"
            label={translations.damage}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.damage}
                error={errors.damage}
                fullWidth
                onChange={onChange('damage')}
              />
            </Editor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.REASON}
            mode="create"
            label={translations.reason}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.reason}
                error={errors.reason}
                fullWidth
                onChange={onChange('reason')}
              />
            </Editor>
          </ConfiguredField>
        </DetailsSection>
      )}

      {showResolutionSection && (
        <DetailsSection
          title={translations.sectionResolution}
          icon="screwdriver-wrench"
        >
          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.IMMEDIATE_ACTION}
            mode="create"
            label={translations.immediateAction}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.immediateAction}
                error={errors.immediateAction}
                fullWidth
                onChange={onChange('immediateAction')}
              />
            </Editor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.ACTION}
            mode="create"
            label={translations.action}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.action}
                error={errors.action}
                fullWidth
                onChange={onChange('action')}
              />
            </Editor>
          </ConfiguredField>

          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.SOLUTION_SUGGESTION}
            mode="create"
            label={translations.suggestedSolution}
            full
          >
            <Editor>
              <TextArea
                scale={2}
                value={form.suggestedSolution}
                error={errors.suggestedSolution}
                fullWidth
                onChange={onChange('suggestedSolution')}
              />
            </Editor>
          </ConfiguredField>

          {/* Closing an issue is its own flow, not a configurable field. */}
          {form.closed && (
            <DetailsField label={translations.solution} full>
              <Editor>
                <TextArea
                  value={form.solution ?? ''}
                  error={errors.solution}
                  fullWidth
                  onChange={onChange('solution')}
                />
              </Editor>
            </DetailsField>
          )}
        </DetailsSection>
      )}

      {showCostSection && (
        <DetailsSection title={translations.sectionCost} icon="coins">
          <ConfiguredField
            config={fieldConfig}
            field={IssueFormField.COST_ESTIMATION}
            mode="create"
            label={translations.costEstimate}
          >
            <CurrencyEditor>
              <NumberInput
                value={form.estimatedCost}
                onChange={onChange('estimatedCost')}
              />
              <div className="currency">NOK</div>
            </CurrencyEditor>
          </ConfiguredField>

          {form.closed && (
            <DetailsField label={translations.actualCost}>
              <CurrencyEditor>
                <NumberInput
                  value={form.actualCost ?? 0}
                  onChange={onChange('actualCost')}
                  error={errors.actualCost}
                />
                <div className="currency">NOK</div>
              </CurrencyEditor>
            </DetailsField>
          )}
        </DetailsSection>
      )}
    </>
  )
}
